import { z } from 'zod'

export const chatStatusSchema = z.enum(['bot', 'waiting_operator', 'operator', 'closed'])
export type ChatStatus = z.infer<typeof chatStatusSchema>

export const chatRoleSchema = z.enum(['visitor', 'bot', 'operator', 'system'])
export type ChatRole = z.infer<typeof chatRoleSchema>

export const chatSystemEventSchema = z.enum(['escalated', 'operator_joined', 'operator_left', 'closed', 'bot_unavailable'])
export type ChatSystemEvent = z.infer<typeof chatSystemEventSchema>

export const chatLocaleSchema = z.enum(['uk', 'en', 'ru', 'sk', 'pl', 'cs', 'hu', 'de', 'bg', 'ro'])
export type ChatLocale = z.infer<typeof chatLocaleSchema>

export const chatMessageSchema = z.object({
  id: z.number().int(),
  role: chatRoleSchema,
  authorName: z.string().nullable(),
  text: z.string(),
  clientId: z.string().nullable(),
  createdAt: z.string(),
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

export const widgetCreateRequestSchema = z.object({
  locale: chatLocaleSchema.catch('uk'),
  pageUrl: z.string().trim().max(500).optional(),
  crmToken: z.string().trim().min(10).max(4000).optional(),
  businessId: z.union([z.string(), z.number()]).transform(String).optional(),
  businessTitle: z.string().trim().max(200).optional(),
})
export type WidgetCreateRequest = z.infer<typeof widgetCreateRequestSchema>

export const widgetCreateResponseSchema = z.object({
  id: z.string(),
  token: z.string(),
})
export type WidgetCreateResponse = z.infer<typeof widgetCreateResponseSchema>

export const widgetMessageRequestSchema = z.object({
  text: z.string().trim().min(1).max(2000),
  clientId: z.string().trim().min(1).max(64),
})
export type WidgetMessageRequest = z.infer<typeof widgetMessageRequestSchema>

export const widgetStateSchema = z.object({
  status: chatStatusSchema,
  operatorName: z.string().nullable(),
  botTyping: z.boolean(),
  messages: z.array(chatMessageSchema),
})
export type WidgetState = z.infer<typeof widgetStateSchema>

export const liveStatusFilterSchema = z.enum(['open', 'waiting', 'operator', 'closed', 'all'])
export type LiveStatusFilter = z.infer<typeof liveStatusFilterSchema>

export const liveConversationSchema = z.object({
  id: z.string(),
  status: chatStatusSchema,
  visitorName: z.string().nullable(),
  visitorEmail: z.string().nullable(),
  businessId: z.string().nullable(),
  businessTitle: z.string().nullable(),
  locale: z.string(),
  pageUrl: z.string().nullable(),
  operatorName: z.string().nullable(),
  operatorEmail: z.string().nullable(),
  unread: z.number().int().nonnegative(),
  lastMessage: z.string().nullable(),
  createdAt: z.string(),
  lastActivityAt: z.string(),
  escalatedAt: z.string().nullable(),
  botTyping: z.boolean(),
})
export type LiveConversation = z.infer<typeof liveConversationSchema>

export const liveListResponseSchema = z.object({
  conversations: z.array(liveConversationSchema),
  available: z.boolean(),
})
export type LiveListResponse = z.infer<typeof liveListResponseSchema>

export const liveConversationDetailSchema = z.object({
  conversation: liveConversationSchema,
  messages: z.array(chatMessageSchema),
})
export type LiveConversationDetail = z.infer<typeof liveConversationDetailSchema>

export const liveSummarySchema = z.object({
  available: z.boolean(),
  waiting: z.number().int().nonnegative(),
  withOperator: z.number().int().nonnegative(),
  open: z.number().int().nonnegative(),
})
export type LiveSummary = z.infer<typeof liveSummarySchema>

export const operatorMessageRequestSchema = z.object({
  text: z.string().trim().min(1).max(2000),
})
export type OperatorMessageRequest = z.infer<typeof operatorMessageRequestSchema>
